import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { setupApiInterceptors } from '../services/api'
import api from '../services/api'
import { VisibilitySettings } from '../types/Pet'
import LoadingSpinner from '../components/Common/LoadingSpinner'

const defaults: VisibilitySettings = {
  showPhoto: true, showBreed: true, showAge: false,
  showMedicalInfo: false, showOwnerContact: false, showEmergencyContact: true,
  lostModeShowPhoto: true, lostModeShowBreed: true, lostModeShowAge: true,
  lostModeShowMedicalInfo: true, lostModeShowOwnerContact: true, lostModeShowEmergencyContact: true,
}

export default function Privacy() {
  const { getAccessTokenSilently } = useAuth0()
  const [settings, setSettings] = useState<VisibilitySettings>(defaults)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [previewMode, setPreviewMode] = useState<'normal' | 'lost'>('normal')

  useEffect(() => {
    setupApiInterceptors(getAccessTokenSilently)
    api.get('/api/users/me/privacy')
      .then(res => setSettings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [getAccessTokenSilently])

  const toggle = async (key: keyof VisibilitySettings) => {
    const updated = { ...settings, [key]: !settings[key] }
    setSettings(updated)
    setSaving(true)
    try {
      await api.put('/api/users/me/privacy', updated)
    } catch (e) {
      console.error('Failed to save privacy settings:', e)
    }
    setSaving(false)
  }

  if (loading) return <LoadingSpinner />

  const s = previewMode === 'lost'
    ? {
        photo: settings.lostModeShowPhoto,
        breed: settings.lostModeShowBreed,
        age: settings.lostModeShowAge,
        medical: settings.lostModeShowMedicalInfo,
        owner: settings.lostModeShowOwnerContact,
        emergency: settings.lostModeShowEmergencyContact,
      }
    : {
        photo: settings.showPhoto,
        breed: settings.showBreed,
        age: settings.showAge,
        medical: settings.showMedicalInfo,
        owner: settings.showOwnerContact,
        emergency: settings.showEmergencyContact,
      }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-2">Privacy Settings</h1>
      <p className="text-gray-500 mb-8">Control what people see when they scan your pet's tag. These settings apply to all your pets.</p>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Settings Column */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Normal Mode</h2>
            <p className="text-sm text-gray-500 mb-4">What's visible when someone scans your pet's tag</p>
            <div className="space-y-3">
              {([
                ['showPhoto', 'Photo'],
                ['showBreed', 'Breed'],
                ['showAge', 'Age'],
                ['showMedicalInfo', 'Medical Information'],
                ['showOwnerContact', 'Owner Contact'],
                ['showEmergencyContact', 'Emergency Contact'],
              ] as [keyof VisibilitySettings, string][]).map(([key, label]) => (
                <label key={key} className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-700">{label}</span>
                  <input type="checkbox" checked={settings[key]} onChange={() => toggle(key)}
                    className="rounded text-primary-500 focus:ring-primary-400 cursor-pointer" />
                </label>
              ))}
            </div>
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Lost Mode</h2>
            <p className="text-sm text-gray-500 mb-4">What's visible when your pet is marked as lost — sharing more helps finders help you</p>
            <div className="space-y-3">
              {([
                ['lostModeShowPhoto', 'Photo'],
                ['lostModeShowBreed', 'Breed'],
                ['lostModeShowAge', 'Age'],
                ['lostModeShowMedicalInfo', 'Medical Information'],
                ['lostModeShowOwnerContact', 'Owner Contact'],
                ['lostModeShowEmergencyContact', 'Emergency Contact'],
              ] as [keyof VisibilitySettings, string][]).map(([key, label]) => (
                <label key={key} className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-700">{label}</span>
                  <input type="checkbox" checked={settings[key]} onChange={() => toggle(key)}
                    className="rounded text-primary-500 focus:ring-primary-400 cursor-pointer" />
                </label>
              ))}
            </div>
          </div>

          {saving && <p className="text-sm text-primary-500">Saving...</p>}
        </div>

        {/* Preview Column */}
        <div>
          <div className="sticky top-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Preview</h2>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setPreviewMode('normal')}
                  className={`text-xs px-3 py-1 rounded-md transition-colors ${previewMode === 'normal' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
                >
                  Normal
                </button>
                <button
                  onClick={() => setPreviewMode('lost')}
                  className={`text-xs px-3 py-1 rounded-md transition-colors ${previewMode === 'lost' ? 'bg-red-500 text-white shadow-sm' : 'text-gray-500'}`}
                >
                  Lost Mode
                </button>
              </div>
            </div>

            <div className="card border-2 border-dashed border-gray-200">
              <p className="text-xs text-gray-400 mb-4 text-center">This is what someone will see when they scan your pet's tag</p>

              {previewMode === 'lost' && (
                <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 rounded-r-lg">
                  <p className="text-red-800 font-bold text-sm">This Pet Is Lost</p>
                  <p className="text-red-700 text-xs">Missing since Mar 23, 2026</p>
                </div>
              )}

              {s.photo && (
                <div className="flex justify-center mb-4">
                  <div className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center">
                    <svg className="w-10 h-10 text-primary-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                </div>
              )}

              <h3 className="text-xl font-bold text-center text-gray-900">Buddy</h3>
              <p className="text-center text-gray-500 text-sm mb-4">Dog</p>

              <div className="space-y-2 text-sm">
                {s.breed && <PreviewRow label="Breed" value="Golden Retriever" />}
                {s.age && <PreviewRow label="Age" value="3 years" />}
                {s.emergency && (
                  <div className="border-t pt-3 mt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Emergency Contact</p>
                    <p className="text-gray-700">Jane Smith</p>
                    <p className="text-primary-500">(555) 123-4567</p>
                  </div>
                )}
                {s.owner && (
                  <div className="border-t pt-3 mt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Owner</p>
                    <p className="text-gray-700">You</p>
                  </div>
                )}
                {s.medical && (
                  <div className="border-t pt-3 mt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Medical</p>
                    <p className="text-gray-700">Allergies: Chicken</p>
                    <p className="text-gray-700">Medications: None</p>
                  </div>
                )}
              </div>

              {!s.photo && !s.breed && !s.age && !s.medical && !s.owner && !s.emergency && (
                <p className="text-center text-gray-400 text-sm py-4">Only name and species will be shown</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-700 font-medium">{value}</span>
    </div>
  )
}
